import { useQuery } from '@tanstack/react-query';
import { httpClient } from '@shared/api';

/** Модель каталога — GET /llm/models (бэкенд V2-2, `llm_catalog.py`). Цены — продажа, ₽. */
export interface LlmModel {
  id: number;
  slug: string;
  title: string;
  vendor_code: string | null;
  vendor_title: string | null;
  logo_url: string | null;
  description: string | null;
  data_in_russia: boolean;
  /** «Рекомендовано RiChat» — прошла замер по порогам. */
  verified: boolean;
  context_tokens: number | null;
  supports_vision: boolean;
  price_in_rub_per_m: string;
  price_out_rub_per_m: string;
  is_default: boolean;
  /** Порядок в списке (решение 31.10): 0 — Yandex, 1 — Рег.облако, 2 — остальные. */
  group?: number;
  eval_score: number | null;
  eval_honesty: number | null;
  eval_seconds: number | null;
  eval_at: string | null;
  /** Цена ответа по замеру (только у замеренных). */
  price_per_answer_rub: string | null;
  /** Оценка цены ответа для любой модели (~3 000 токенов вход, ~300 выход). */
  price_per_answer_estimate_rub: string | null;
}

export const llmQueryKeys = { models: ['llm', 'models'] as const };

export function useModels() {
  return useQuery<LlmModel[], Error>({
    queryKey: llmQueryKeys.models,
    queryFn: async () => (await httpClient.get<LlmModel[]>('/llm/models')).data,
    staleTime: 10 * 60_000,
  });
}

/** Цена ответа для показа: замер, иначе оценка с «≈». */
export function answerPrice(m: LlmModel): { value: number | null; approx: boolean } {
  if (m.price_per_answer_rub != null)
    return { value: Number(m.price_per_answer_rub), approx: false };
  if (m.price_per_answer_estimate_rub != null)
    return { value: Number(m.price_per_answer_estimate_rub), approx: true };
  return { value: null, approx: true };
}

/** Быстрый выбор (экраны 2 и 10): самая выгодная, самая точная, лучшая российская из рекомендованных. */
export function quickPicks(models: LlmModel[]) {
  const rec = models.filter(
    (m) => m.verified && m.eval_score != null && m.price_per_answer_rub != null,
  );
  const price = (m: LlmModel) => Number(m.price_per_answer_rub);
  const accurate = [...rec].sort(
    (a, b) => (b.eval_score ?? 0) - (a.eval_score ?? 0) || price(a) - price(b),
  )[0];
  // Три разные модели: «выгодно» — самая дешёвая из остальных, если самая точная ещё и самая дешёвая.
  const cheapest = [...rec]
    .filter((m) => m.id !== accurate?.id)
    .sort((a, b) => price(a) - price(b) || (b.eval_score ?? 0) - (a.eval_score ?? 0))[0];
  const russian = [...rec]
    .filter((m) => m.data_in_russia)
    .sort((a, b) => (b.eval_score ?? 0) - (a.eval_score ?? 0) || price(a) - price(b))[0];
  const def = models.find((m) => m.is_default);
  return { cheapest, accurate, russian, def };
}

/** Русские названия разработчиков — для поиска («Яндекс», «Гугл»…). */
const VENDOR_RU: Record<string, string> = {
  yandex: 'яндекс',
  google: 'гугл',
  openai: 'опенай чатгпт',
  anthropic: 'антропик клод',
  deepseek: 'дипсик',
  qwen: 'алибаба квен',
  'x-ai': 'грок',
  mistralai: 'мистраль',
  moonshotai: 'кими',
};

/**
 * Поиск модели «по названию и задаче» (экраны 4, 10, 11): название, разработчик (и по-русски),
 * описание и признаки словами — «картинки», «россия».
 */
export function modelMatches(m: LlmModel, q: string): boolean {
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const tags = [
    m.supports_vision ? 'картинки изображения фото' : '',
    m.data_in_russia ? 'россия российская' : '',
    VENDOR_RU[m.vendor_code ?? ''] ?? '',
  ].join(' ');
  const s =
    `${m.title} ${m.vendor_title ?? ''} ${m.slug} ${m.description ?? ''} ${tags}`.toLowerCase();
  return words.every((w) => s.includes(w));
}

/** Короткое описание для карточек: первая фраза, без повтора названия модели. */
export function shortModelDesc(m: LlmModel, max = 70): string {
  let d = (m.description ?? '').trim();
  if (d.toLowerCase().startsWith(m.title.toLowerCase()))
    d = d.slice(m.title.length).replace(/^[\s—–-]+/, '');
  const first = d.split(/(?<=[.!?])\s/)[0] ?? d;
  const s = first.replace(/\.$/, '');
  return s.length > max ? s.slice(0, max - 2).trimEnd() + '…' : s;
}
